import { useQuery } from "@tanstack/react-query";
import { BosDurum, Buton, Iskelet, Kart, Rozet } from "@koclukpanel/ui";
import { ApiHatasi, guvenlikPanosu, type GuvenlikDurumu, type PanoMaddesi } from "./api";

/**
 * GÜVENLİK DURUM PANOSU — F-SEC-01
 *
 * ================================================================
 * HER MADDE TIKLANABİLİR
 * ================================================================
 * Şartname: *"Her madde tıklanabilir ve çözüldüğü yere gider.
 * Tıklanamayan bir sayı bir gösterge değil süstür."*
 *
 * Bu depoda tam olarak o hata ölçüldü: 25 alan ekranda duruyordu ve
 * hiçbirine tıklanamıyordu (C38). O yüzden burada "gidilecek ekran"
 * isteğe bağlı bir süs değil, sunucudan gelen zorunlu bir alan — ve
 * aşağıdaki bileşen onu düğme yapıyor.
 *
 * ================================================================
 * PUAN YOK
 * ================================================================
 * "%87 güvenli" gibi bir sayı ÜRETİLMİYOR. Açıklanamayan bir güvenlik
 * puanı yanlış bir güven duygusu üretir; burada gösterilen şey
 * sayılabilir maddeler ve her birinin neden o durumda olduğu.
 */

/*
  `onay` — "basari" DEGIL. Rozet sozlugunde bes ton var: notr, bilgi,
  onay, uyari, hata. "basari" diye bir ton yok ve TypeScript bunu
  yakaladi; yakalamasaydi Tailwind tanimsiz sinifi sessizce atar ve
  rozet renksiz cikardi (bu depoda bugun bir kez oldu).
*/
const DURUM_ROZETI: Record<GuvenlikDurumu, { ton: "onay" | "uyari" | "hata"; metin: string }> = {
  iyi: { ton: "onay", metin: "sorun yok" },
  dikkat: { ton: "uyari", metin: "bakılmalı" },
  acil: { ton: "hata", metin: "acil" },
};

const GENEL_CUMLE: Record<GuvenlikDurumu, string> = {
  iyi: "Bakılması gereken bir şey görünmüyor.",
  dikkat: "Bakılması gereken maddeler var. Hiçbiri acil değil, ama bekletilmemeli.",
  acil: "Acil bakılması gereken bir madde var. Aşağıda kırmızı işaretli.",
};

/**
 * Etiketin sayıyla nasıl birleşeceği.
 *
 * ================================================================
 * MÜHÜR MADDESİ ÖZEL — VE ÖZEL OLMASI GEREKİYOR
 * ================================================================
 * Öteki maddeler "8 personel …" diye okunuyor. Mühür maddesi ise
 * "25 saat önce denetim mührü atıldı" — sayı cümlenin BAŞINDA ve
 * "hiç atılmamış" hâlinde sayı diye bir şey YOK.
 *
 * `0` yazmak yanlış olurdu: "0 saat önce atıldı" ile "hiç atılmamış"
 * birbirinin tam tersi. Sunucu bu ayrımı etikete koyuyor, ekran da
 * etiketten okuyor.
 */
export function maddeMetni(madde: PanoMaddesi): string {
  if (madde.etiket.startsWith("denetim mührü")) return madde.etiket;
  return `${madde.sayi} ${madde.etiket}`;
}

function MaddeSatiri({ madde, onGit }: { madde: PanoMaddesi; onGit: (ekran: string) => void }) {
  const rozet = DURUM_ROZETI[madde.durum];
  return (
    <div className="border-notr-200 flex flex-col gap-2 border-b py-3 last:border-b-0">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <p className="text-notr-900 text-sm font-medium">{maddeMetni(madde)}</p>
          <p className="text-notr-600 mt-0.5 text-xs">{madde.aciklama}</p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <Rozet ton={rozet.ton}>{rozet.metin}</Rozet>
          {/*
            SORUN YOKKEN DE TIKLANABİLİR. "Yalnızca sorun varsa düğme
            koy" demek, yöneticinin doğrulama yapmasını engellerdi:
            "sıfır kilitli hesap" cümlesine güvenmek yerine listeye
            bakabilmeli. Gösterge, kapattığı soruyu açabilmeli.
          */}
          <Buton gorunum="ikincil" onClick={() => onGit(madde.ekran)}>
            Bak
          </Buton>
        </div>
      </div>
    </div>
  );
}

export function GuvenlikPano({ onGit }: { onGit: (ekran: string) => void }) {
  const veri = useQuery({ queryKey: ["guvenlik-pano"], queryFn: guvenlikPanosu, retry: false });

  if (veri.isPending) return <Iskelet satir={5} />;

  if (veri.isError) {
    return (
      <Kart baslik="Güvenlik durumu">
        <BosDurum
          ton="hata"
          baslik="Durum panosu alınamadı"
          aciklama={veri.error instanceof ApiHatasi ? veri.error.message : undefined}
          eylem={
            <Buton gorunum="ikincil" onClick={() => void veri.refetch()}>
              Tekrar dene
            </Buton>
          }
        />
      </Kart>
    );
  }

  const { genel, maddeler } = veri.data;

  return (
    <Kart
      baslik="Güvenlik durumu"
      ton={genel === "acil" ? "hata" : genel === "dikkat" ? "uyari" : undefined}
      aciklama={GENEL_CUMLE[genel]}
      eylem={<Rozet ton={DURUM_ROZETI[genel].ton}>{DURUM_ROZETI[genel].metin}</Rozet>}
    >
      <div className="flex flex-col">
        {maddeler.map((m) => (
          <MaddeSatiri key={m.anahtar} madde={m} onGit={onGit} />
        ))}
      </div>
      <p className="text-notr-600 mt-3 text-xs">
        Genel durum, maddelerin ortalaması değil en kötüsüdür: dört madde iyi, biri acilse pano acil
        der — çünkü bakılması gereken o birdir.
      </p>
    </Kart>
  );
}
